/* eslint-disable react/no-unescaped-entities */
import { basePath } from '@/lib/base';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { ShoppingBag, Store, Package, ArrowUpRight, ArrowRight, Calendar, ChevronDown, RefreshCw, Loader2, Receipt } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ADMIN } from '@/constants/testIds';
import { StoreFilter } from '@/components/StoreFilter';
import { PaymentStatus } from '@/components/admin/PaymentStatus';
import { formatMoney as money } from '@/lib/adminFormat';

const itemName = (i) => i.selected_name || i.vitrine_name || i.shopify_name || 'Produit';

const CUR_SYM = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const sym = (c) => CUR_SYM[(c || 'eur').toLowerCase()] || (c || '').toUpperCase();
const StatCard = ({ icon: Icon, label, value, testid, sub }) => (
  <div className="dashboard-metric" data-testid={`${testid}-metric`}>
    <div className="dashboard-metric-label">{label}<Icon aria-hidden="true" /></div>
    <div className="dashboard-metric-value" data-testid={testid}>{value}</div>
    {sub && <div className="dashboard-metric-sub" data-testid={`${testid}-sub`}>{sub}</div>}
  </div>
);

// Build an ISO range [00:00:00, +24h) for a given YYYY-MM-DD (browser local TZ).
// We pass these ISO strings to the backend which compares them against
// `created_at` strings — works because both are ISO with TZ offsets.
function dayRange(yyyymmdd) {
  if (!yyyymmdd) return { since: '', until: '' };
  const start = new Date(`${yyyymmdd}T00:00:00`);
  const end = new Date(start.getTime() + 24 * 3600 * 1000);
  return { since: start.toISOString(), until: end.toISOString() };
}

const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
};

// Resolve a period preset (or a specific day / custom range) into an ISO
// [since, until) window the backend can compare against `created_at`.
function periodRange(period, day, from, to) {
  const now = new Date();
  const startOfToday = new Date(`${todayStr()}T00:00:00`);
  const tomorrow = new Date(startOfToday.getTime() + 24 * 3600 * 1000);
  const mk = (s, e) => ({ since: s ? s.toISOString() : '', until: e ? e.toISOString() : '' });
  switch (period) {
    case 'today': return mk(startOfToday, tomorrow);
    case 'yesterday': return mk(new Date(startOfToday.getTime() - 24 * 3600 * 1000), startOfToday);
    case '7d': return mk(new Date(startOfToday.getTime() - 6 * 24 * 3600 * 1000), tomorrow);
    case '30d': return mk(new Date(startOfToday.getTime() - 29 * 24 * 3600 * 1000), tomorrow);
    case 'month': return mk(new Date(now.getFullYear(), now.getMonth(), 1), tomorrow);
    case 'lastmonth': return mk(new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1));
    case 'day': return dayRange(day);
    case 'custom': return mk(
      from ? new Date(`${from}T00:00:00`) : null,
      to ? new Date(new Date(`${to}T00:00:00`).getTime() + 24 * 3600 * 1000) : null,
    );
    default: return { since: '', until: '' };
  }
}

const PERIOD_LABELS = {
  today: "Aujourd'hui", yesterday: 'Hier', '7d': '7 derniers jours',
  '30d': '30 derniers jours', month: 'Ce mois-ci', lastmonth: 'Mois dernier', all: 'Toute la période',
};
function periodLabel(period, day, from, to) {
  if (period === 'day') return day ? new Date(`${day}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Jour précis';
  if (period === 'custom') return (from && to)
    ? `Du ${new Date(`${from}T12:00:00`).toLocaleDateString('fr-FR')} au ${new Date(`${to}T12:00:00`).toLocaleDateString('fr-FR')}`
    : 'Période personnalisée';
  return PERIOD_LABELS[period] || 'Toute la période';
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tx, setTx] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  // Default view = current day. Period selector adds ranges (7/30 days, month…).
  const [period, setPeriod] = useState('today');
  const [day, setDay] = useState(todayStr());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(''); // '' = toutes les boutiques
  const [curSel, setCurSel] = useState(''); // selected display currency for the revenue KPI
  const [curFilter, setCurFilter] = useState(''); // '' = all currencies; otherwise the page is filtered on it
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [storesError, setStoresError] = useState(false);

  const range = useMemo(() => periodRange(period, day, from, to), [period, day, from, to]);

  useEffect(() => {
    let active = true;
    api.get('/stores').then((r) => { if (active) { setStores(r.data); setStoresError(false); } }).catch(() => { if (active) setStoresError(true); });
    return () => { active = false; };
  }, [refreshKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    const params = {};
    if (range.since) params.since = range.since;
    if (range.until) params.until = range.until;
    if (storeId) params.store_id = storeId;
    Promise.all([
      api.get('/stats', { params }),
      api.get('/transactions', { params: { ...params, limit: 50, ...(curFilter ? { currency: curFilter } : {}) } }),
    ]).then(([summary, transactions]) => {
      if (!active) return;
      setStats(summary.data); setTx(transactions.data); setExpandedId(null);
    }).catch(() => {
      if (!active) return;
      setStats(null); setTx([]); setLoadError('Les données ne sont pas disponibles pour le moment.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [range.since, range.until, storeId, refreshKey, curFilter]);

  const revCurrencies = useMemo(() => stats?.revenue_by_currency || [], [stats]);
  // Keep the selected currency valid as data changes; default to the top-revenue one.
  useEffect(() => {
    if (!revCurrencies.length) return;
    if (!revCurrencies.some((r) => r.currency === curSel)) setCurSel(revCurrencies[0].currency);
  }, [revCurrencies, curSel]);
  const revRow = revCurrencies.find((r) => r.currency === curSel) || revCurrencies[0] || null;
  // Clicking a currency = filter the whole page on it (KPI + sales list) and hide the others.
  const pickCurrency = (c) => { setCurSel(c); setCurFilter((f) => (f === c ? '' : c)); };
  const visibleCurrencies = curFilter ? revCurrencies.filter((r) => r.currency === curFilter) : revCurrencies;

  const isFiltered = period !== 'all';
  const filterLabel = periodLabel(period, day, from, to);
  const revenueSub = period === 'all' ? 'Total cumulé' : filterLabel;

  return (
    <div data-testid="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <div className="dashboard-eyebrow">Pilotage</div>
          <h1 data-testid="dashboard-title">Vue d'ensemble</h1>
          <p className="dashboard-caption first-letter:uppercase" data-testid="dashboard-period-label">{filterLabel}</p>
        </div>
        <Link to={`${basePath()}/transactions`} className="dashboard-action" data-testid="dashboard-view-transactions">Voir les transactions<ArrowUpRight className="w-3.5 h-3.5" /></Link>
      </div>

      {/* Period + day/range selector */}
      <section className="dashboard-filters" aria-label="Filtres de l'activité">
        <div className="flex flex-wrap items-end gap-3" data-testid="dashboard-day-selector">
          <div>
            <div className="overline mb-2" id="dashboard-store-label">Boutique</div>
            <StoreFilter stores={stores} value={storeId} onChange={setStoreId} testId="dashboard-store-filter" />
          </div>
          <div>
            <div className="overline mb-2" id="dashboard-period-field-label">Période</div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-10 w-48 rounded-md text-sm" data-testid="dashboard-period-select" aria-labelledby="dashboard-period-field-label">
                <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-zinc-400" /><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today" data-testid="period-today">Aujourd'hui</SelectItem>
                <SelectItem value="yesterday" data-testid="period-yesterday">Hier</SelectItem>
                <SelectItem value="7d" data-testid="period-7d">7 derniers jours</SelectItem>
                <SelectItem value="30d" data-testid="period-30d">30 derniers jours</SelectItem>
                <SelectItem value="month" data-testid="period-month">Ce mois-ci</SelectItem>
                <SelectItem value="lastmonth" data-testid="period-lastmonth">Mois dernier</SelectItem>
                <SelectItem value="day" data-testid="period-day">Jour précis…</SelectItem>
                <SelectItem value="custom" data-testid="period-custom">Période personnalisée…</SelectItem>
                <SelectItem value="all" data-testid="period-all">Toute la période</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === 'day' && (
            <div>
              <label htmlFor="dashboard-day" className="overline block mb-2">Jour</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-zinc-500" />
                <Input
                  type="date"
                  id="dashboard-day"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  max={todayStr()}
                  className="h-10 w-44 rounded-md font-mono text-sm"
                  data-testid="dashboard-day-input"
                />
              </div>
            </div>
          )}
          {period === 'custom' && (
            <>
              <div>
                <label htmlFor="dashboard-from" className="overline block mb-2">Du</label>
                <Input
                  type="date"
                  id="dashboard-from"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  max={to || todayStr()}
                  className="h-10 w-44 rounded-md font-mono text-sm"
                  data-testid="dashboard-from-input"
                />
              </div>
              <div>
                <label htmlFor="dashboard-to" className="overline block mb-2">Au</label>
                <Input
                  type="date"
                  id="dashboard-to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  min={from || undefined}
                  max={todayStr()}
                  className="h-10 w-44 rounded-md font-mono text-sm"
                  data-testid="dashboard-to-input"
                />
              </div>
            </>
          )}
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} disabled={loading} className="admin-icon-button sm:ml-auto" aria-label="Actualiser les données" title="Actualiser" data-testid="dashboard-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </section>
      {(loadError || storesError) && <div className="dashboard-feedback" role="alert" data-testid="dashboard-load-error"><span>{loadError || 'Impossible de charger les boutiques.'}</span><button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="underline shrink-0" data-testid="dashboard-retry">Réessayer</button></div>}

      <section className="dashboard-metrics" aria-label="Indicateurs clés" aria-busy={loading} data-testid="dashboard-metrics">
        <div className="dashboard-metric dashboard-revenue" data-testid="dashboard-revenue-card">
              <div className="dashboard-metric-label">
                <span>Chiffre d'affaires</span>
                {!loading && revCurrencies.length > 1 && (
                  <Select value={curFilter || 'all'} onValueChange={(v) => { if (v === 'all') setCurFilter(''); else { setCurSel(v); setCurFilter(v); } }}>
                    <SelectTrigger data-testid="dashboard-currency-select" aria-label="Devise du chiffre d'affaires" className="h-7 w-auto gap-1 border-zinc-200 px-2 py-0 text-[10px] uppercase">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="dashboard-currency-opt-all" className="text-xs">Toutes les devises</SelectItem>
                      {revCurrencies.map((r) => (
                        <SelectItem key={r.currency} value={r.currency} data-testid={`dashboard-currency-opt-${r.currency}`} className="text-xs">
                          {r.currency.toUpperCase()} ({sym(r.currency)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="dashboard-metric-value" data-testid={ADMIN.statRevenue}>
                {loading || loadError ? '—' : revRow ? money(revRow.total, revRow.currency) : money(0, stores.find((s) => s.id === storeId)?.currency || 'eur')}
              </div>
              <div className="dashboard-metric-sub" data-testid="dashboard-revenue-sub">
                {loading ? 'Chargement…' : revRow ? `${revRow.count} commande${revRow.count > 1 ? 's' : ''} · ${revenueSub}${curFilter ? ` · ${curFilter.toUpperCase()} uniquement` : ''}` : revenueSub}
              </div>
              {!loading && revCurrencies.length > 1 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="dashboard-currency-chips">
                  {visibleCurrencies.map((r) => (
                    <button key={r.currency} type="button" onClick={() => pickCurrency(r.currency)}
                      data-testid={`dashboard-currency-chip-${r.currency}`} title={curFilter === r.currency ? 'Cliquer pour afficher toutes les devises' : `Filtrer la page sur ${r.currency.toUpperCase()}`}
                      aria-pressed={r.currency === curFilter} className="dashboard-currency-chip">
                      {money(r.total, r.currency)}
                    </button>
                  ))}
                  {curFilter && <button type="button" onClick={() => setCurFilter('')} className="text-[11px] text-zinc-500 hover:text-zinc-900 underline underline-offset-2" data-testid="dashboard-currency-clear">Toutes les devises</button>}
                </div>
              )}
        </div>
        <StatCard
          icon={ShoppingBag}
          label="Commandes payées"
          value={loading ? '—' : (curFilter && revRow ? revRow.count : stats?.total_orders) ?? '—'}
          sub={!loading && stats ? (curFilter ? `Payées en ${curFilter.toUpperCase()}` : `${stats.total_transactions} sessions de paiement`) : 'Sur la période'}
          testid={ADMIN.statOrders}
        />
        <StatCard
          icon={Store}
          label="Boutiques"
          value={loading ? '—' : stats?.stores_count ?? '—'}
          sub="Dans cet espace"
          testid={ADMIN.statStores}
        />
        <StatCard
          icon={Package}
          label="Produits"
          value={loading ? '—' : stats?.products_count ?? '—'}
          sub="Au catalogue"
          testid={ADMIN.statProducts}
        />
      </section>

      <section className="dashboard-activity" data-testid="dashboard-activity" aria-busy={loading}>
        <div className="dashboard-activity-heading">
          <div>
            <h2 data-testid="dashboard-activity-title">Activité récente <span data-testid="dashboard-tx-count" className="text-xs text-zinc-500 ml-2 font-sans font-normal">{loading ? '—' : tx.length >= 50 ? '50+' : tx.length}</span></h2>
            <div className="dashboard-caption" data-testid="dashboard-activity-period">{isFiltered ? filterLabel : 'Dernières transactions'}</div>
          </div>
          <Link to={`${basePath()}/transactions`} className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900" data-testid="dashboard-all-activity">Tout voir<ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        {loading ? <div className="dashboard-loading" role="status" data-testid="dashboard-loading"><Loader2 className="h-4 w-4 animate-spin" />Chargement de l'activité…</div> : loadError ? null : <>
        <div className="dashboard-table-wrap hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <th className="px-6 py-3 overline font-medium">Date</th>
                <th className="px-6 py-3 overline font-medium">Client</th>
                <th className="px-6 py-3 overline font-medium">Montant</th>
                <th className="px-6 py-3 overline font-medium">Statut</th>
                <th className="px-6 py-3 overline font-medium">Commande</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-zinc-500" data-testid="dashboard-empty">
                  <Receipt className="w-6 h-6 mx-auto mb-3 text-zinc-400" />
                  {isFiltered ? 'Aucune transaction sur cette période' : "Aucune transaction pour l'instant"}
                </td></tr>
              ) : tx.map((t) => {
                const open = expandedId === t.id;
                return (
                <React.Fragment key={t.id}>
                <tr
                  onClick={() => setExpandedId(open ? null : t.id)}
                  data-testid={`dashboard-tx-row-${t.id}`}
                  tabIndex={0}
                  aria-expanded={open}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedId(open ? null : t.id); } }}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${open ? 'bg-zinc-50' : ''}`}
                >
                  <td className="px-6 py-3 font-mono text-xs whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                      {new Date(t.created_at).toLocaleString('fr-FR')}
                    </span>
                  </td>
                  <td className="px-6 py-3">{t.customer?.email || '—'}</td>
                  <td data-testid={`dashboard-tx-amount-${t.id}`} className="px-6 py-3 font-mono font-medium whitespace-nowrap">{money(t.amount, t.currency)}</td>
                  <td className="px-6 py-3">
                    <PaymentStatus status={t.payment_status} testId={`dashboard-tx-status-${t.id}`} />
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">
                    {t.shopify_order_name || (t.shopify_error ? '⚠️' : '—')}
                  </td>
                </tr>
                {open && (
                  <tr className="bg-zinc-50/60" data-testid={`dashboard-tx-detail-${t.id}`}>
                    <td colSpan={5} className="px-6 pb-5 pt-1">
                      <div className="grid sm:grid-cols-3 gap-5 text-xs py-4">
                        <div className="sm:col-span-1">
                          <div className="overline mb-2">Articles commandés</div>
                          <div className="space-y-1.5" data-testid={`dashboard-tx-items-${t.id}`}>
                            {(t.items || []).length === 0 ? (
                              <div className="text-zinc-400">Aucun article</div>
                            ) : t.items.map((i, idx) => (
                              <div key={idx} className="flex justify-between gap-2">
                                <span className="min-w-0">
                                  <span className="font-medium text-zinc-800">{itemName(i)}</span>
                                  {i.variant_title && <span className="text-zinc-500"> — {i.variant_title}</span>}
                                  <span className="font-mono text-zinc-400"> ×{i.quantity}</span>
                                  {i.bogo_discount > 0 && <span className="text-emerald-600 ml-1">🎁 −{money(i.bogo_discount, t.currency)}</span>}
                                </span>
                                <span className="font-mono shrink-0 text-zinc-600">{money(i.price * i.quantity, t.currency)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="overline mb-2">Client</div>
                          <div className="space-y-0.5 text-zinc-700">
                            <div className="font-medium">{t.customer?.first_name} {t.customer?.last_name}</div>
                            <div className="break-all">{t.customer?.email}</div>
                            {t.customer?.phone && <div className="font-mono">{t.customer.phone}</div>}
                            <div className="text-zinc-500">{t.customer?.address1}, {t.customer?.postal_code} {t.customer?.city}, {t.customer?.country}</div>
                          </div>
                        </div>
                        <div>
                          <div className="overline mb-2">Paiement</div>
                          <div className="space-y-1 font-mono text-zinc-600">
                            {t.vitrine_metadata?.subtotal != null && <div className="flex justify-between"><span>Sous-total</span><span>{money(t.vitrine_metadata.subtotal, t.currency)}</span></div>}
                            {t.vitrine_metadata?.bogo_discount > 0 && <div className="flex justify-between text-emerald-600"><span>Remise 2ème</span><span>−{money(t.vitrine_metadata.bogo_discount, t.currency)}</span></div>}
                            {t.vitrine_metadata?.shipping != null && <div className="flex justify-between"><span>Livraison</span><span>{money(t.vitrine_metadata.shipping, t.currency)}</span></div>}
                            <div className="flex justify-between font-bold text-zinc-900 pt-1 border-t border-zinc-100"><span>Total</span><span>{money(t.amount, t.currency)}</span></div>
                            {t.shopify_order_name && <div className="pt-1 text-zinc-500">Shopify : {t.shopify_order_name}</div>}
                            {t.shopify_error && <div className="pt-1 text-amber-600 font-sans">⚠ {t.shopify_error}</div>}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100" data-testid="dashboard-tx-mobile-list">
          {tx.length === 0 ? (
            <div className="px-4 py-10 text-center text-zinc-500" data-testid="dashboard-empty-mobile">
              {isFiltered ? 'Aucune transaction sur cette période' : "Aucune transaction pour l'instant"}
            </div>
          ) : tx.map((t) => {
            const open = expandedId === t.id;
            return (
              <div key={t.id}>
                <button
                  type="button"
                  onClick={() => setExpandedId(open ? null : t.id)}
                  data-testid={`dashboard-tx-card-${t.id}`}
                  aria-expanded={open}
                  className="w-full text-left px-4 py-3.5 active:bg-zinc-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.customer?.email || '—'}</div>
                      <div className="text-[11px] text-zinc-400 font-mono mt-0.5">{new Date(t.created_at).toLocaleString('fr-FR')}</div>
                    </div>
                    <div data-testid={`dashboard-tx-mobile-amount-${t.id}`} className="font-mono font-semibold shrink-0">{money(t.amount, t.currency)}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <PaymentStatus status={t.payment_status} testId={`dashboard-tx-mobile-status-${t.id}`} />
                    {t.shopify_order_name && <span className="text-[11px] font-mono text-zinc-500">{t.shopify_order_name}</span>}
                  </div>
                </button>
                {open && (
                  <div className="px-4 pb-4 text-xs" data-testid={`dashboard-tx-card-detail-${t.id}`}>
                    <div className="border-t border-zinc-200 pt-3 space-y-1.5">
                      {(t.items || []).map((i, idx) => (
                        <div key={idx} className="flex justify-between gap-2">
                          <span className="min-w-0 truncate"><span className="font-medium text-zinc-800">{itemName(i)}</span><span className="font-mono text-zinc-400"> ×{i.quantity}</span></span>
                          <span className="font-mono shrink-0 text-zinc-600">{money(i.price * i.quantity, t.currency)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-bold text-zinc-900 pt-1.5 border-t border-zinc-100"><span>Total</span><span className="font-mono">{money(t.amount, t.currency)}</span></div>
                      {t.customer?.first_name && <div className="pt-1 text-zinc-500">{t.customer.first_name} {t.customer.last_name} · {t.customer.city} {t.customer.country}</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>}
      </section>
    </div>
  );
}
